// EditPartnerForm.jsx avec les nouveaux champs

import React, { useEffect, useState, useContext } from 'react';
import { ApiContext } from '../../App';
import { toast } from 'react-toastify';

const EditPartnerForm = ({ partner, isOpen, onClose, onEditSuccess }) => {
  const API = useContext(ApiContext);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const [editFormData, setEditFormData] = useState({
    name: '',
    description: '',
    contacts: '',
    city: '',
    internalManager: '',
    contractSigned: false,
    technologies: '',      
    tags: '',             
    status: 'cold',       
    linkedinUrl: ''       
  });

  // Pré-remplir le formulaire lorsque le modal s'ouvre
  useEffect(() => {
    if (partner && isOpen) {
      setEditFormData({
        name: partner.name || '',
        description: partner.description || '',
        contacts: partner.contacts ? partner.contacts.join(', ') : '',
        city: partner.city || '',
        internalManager: partner.internalManager || '',
        contractSigned: partner.contractSigned || false,
        technologies: partner.technologies ? partner.technologies.join(', ') : '',      // NOUVEAU
        tags: partner.tags ? partner.tags.join(', ') : '',                             // NOUVEAU
        status: partner.status || 'cold',                                              // NOUVEAU                                  // NOUVEAU
        linkedinUrl: partner.linkedinUrl || ''                                         // NOUVEAU
      });
      setErrors({});
    }
  }, [partner, isOpen]);

  // Gérer les changements d'input
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    
    // Effacer l'erreur du champ modifié
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  // Validation du formulaire
  const validateForm = () => {
    const newErrors = {};
    
    if (!editFormData.name.trim()) {
      newErrors.name = 'Le nom de l\'entreprise est requis';
    }
  
    
    if (editFormData.contacts) {
      const contacts = editFormData.contacts.split(',').map(c => c.trim());
      const invalidContacts = contacts.filter(contact => contact && !contact.includes('@'));
      if (invalidContacts.length > 0) {
        newErrors.contacts = 'Veuillez saisir des adresses email valides séparées par des virgules';
      }
    }

    // Validation LinkedIn URL
    if (editFormData.linkedinUrl && !editFormData.linkedinUrl.includes('linkedin.com')) {
      newErrors.linkedinUrl = 'Veuillez saisir une URL LinkedIn valide';
    }

    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Gestion de la soumission du formulaire
  const handleEditFormSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      const updatedPartner = {
        ...editFormData,
        contacts: editFormData.contacts
          ? editFormData.contacts.split(',').map((contact) => contact.trim()).filter(Boolean)
          : [],
        technologies: editFormData.technologies
          ? editFormData.technologies.split(',').map((tech) => tech.trim()).filter(Boolean)
          : [],
        tags: editFormData.tags
          ? editFormData.tags.split(',').map((tag) => tag.trim()).filter(Boolean)
          : [],
      };
      
      const response = await API.put(`/api/partners/${partner._id}`, updatedPartner);
      
      toast.success('Entreprise mise à jour avec succès !');
      onEditSuccess(response.data);
      onClose();
    } catch (error) {
      console.error('Error updating partner:', error);
      toast.error('Échec de la mise à jour de l\'entreprise. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  // Gérer la fermeture du modal
  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  // Gérer l'appui sur Escape
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen && !loading) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, loading, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div 
        className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-indigo-500 to-blue-600 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                <i className="bi bi-pencil-square text-white text-lg"></i>
              </div>
              <div>
                <h2 className="text-xl font-bold text-white m-0">Modifier l'Entreprise</h2>
                <p className="text-indigo-100 text-sm">Mettre à jour les informations et paramètres de l'entreprise</p>
              </div>
            </div>
            
            <button
              onClick={handleClose}
              disabled={loading}
              className="w-8 h-8 bg-white/20 hover:bg-white/30 rounded-lg flex items-center justify-center 
                       text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <i className="bi bi-x-lg"></i>
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 p-6 overflow-y-auto min-h-0">
          <form onSubmit={handleEditFormSubmit} className="space-y-6">
            {/* Partner Name */}
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-slate-700">
                <i className="bi bi-building mr-2 text-slate-500"></i>
                Nom de l'entreprise *
              </label>
              <input
                type="text"
                name="name"
                value={editFormData.name}
                onChange={handleInputChange}
                className={`w-full px-4 py-3 border rounded-lg transition-colors focus:outline-none focus:ring-2 
                           ${errors.name 
                             ? 'border-red-300 focus:ring-red-500 focus:border-red-500' 
                             : 'border-slate-300 focus:ring-indigo-500 focus:border-indigo-500'
                           }`}
                placeholder="Saisir le nom de l'entreprise"
                disabled={loading}
              />
              {errors.name && (
                <p className="text-sm text-red-600 flex items-center gap-1">
                  <i className="bi bi-exclamation-circle"></i>
                  {errors.name}
                </p>
              )}
            </div>

            {/* Description */}
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-slate-700">
                <i className="bi bi-file-text mr-2 text-slate-500"></i>
                Description
              </label>
              <textarea
                name="description"
                value={editFormData.description}
                onChange={handleInputChange}
                rows={4}
                className={`w-full px-4 py-3 border rounded-lg transition-colors focus:outline-none focus:ring-2 resize-none
                           ${errors.description 
                             ? 'border-red-300 focus:ring-red-500 focus:border-red-500' 
                             : 'border-slate-300 focus:ring-indigo-500 focus:border-indigo-500'
                           }`}
                placeholder="Décrire l'activité de l'entreprise et la relation"
                disabled={loading}
              />
            </div>

            {/* Three column layout for basic info */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* City */}
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-slate-700">
                  <i className="bi bi-geo-alt mr-2 text-slate-500"></i>
                  Ville
                </label>
                <input
                  type="text"
                  name="city"
                  value={editFormData.city}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg transition-colors 
                           focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="Saisir la ville"
                  disabled={loading}
                />
              </div>

              {/* Status */}
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-slate-700">
                  <i className="bi bi-flag mr-2 text-slate-500"></i>
                  Statut
                </label>
                <select
                  name="status"
                  value={editFormData.status}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg transition-colors 
                           focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  disabled={loading}
                >
                  <option value="cold">Cold</option>
                  <option value="prospect">Prospect</option>
                  <option value="client">Client</option>
                  <option value="old client">Ancien client</option>
                </select>
              </div>


            </div>

            {/* Two column layout for manager and linkedin */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Internal Manager */}
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-slate-700">
                  <i className="bi bi-person mr-2 text-slate-500"></i>
                  Responsable du compte
                </label>
                <input
                  type="text"
                  name="internalManager"
                  value={editFormData.internalManager}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg transition-colors 
                           focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="Assigner un responsable interne"
                  disabled={loading}
                />
              </div>

              {/* LinkedIn URL */}
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-slate-700">
                  <i className="bi bi-linkedin mr-2 text-slate-500"></i>
                  URL LinkedIn
                </label>
                <input
                  type="url"
                  name="linkedinUrl"
                  value={editFormData.linkedinUrl}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-3 border rounded-lg transition-colors focus:outline-none focus:ring-2 
                             ${errors.linkedinUrl 
                               ? 'border-red-300 focus:ring-red-500 focus:border-red-500' 
                               : 'border-slate-300 focus:ring-indigo-500 focus:border-indigo-500'
                             }`}
                  placeholder="https://linkedin.com/company/..."
                  disabled={loading}
                />
                {errors.linkedinUrl && (
                  <p className="text-sm text-red-600 flex items-center gap-1">
                    <i className="bi bi-exclamation-circle"></i>
                    {errors.linkedinUrl}
                  </p>
                )}
              </div>
            </div>

            {/* Technologies */}
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-slate-700">
                <i className="bi bi-code-slash mr-2 text-slate-500"></i>
                Technologies
              </label>
              <input
                type="text"
                name="technologies"
                value={editFormData.technologies}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-slate-300 rounded-lg transition-colors 
                         focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                placeholder="React, Node.js, MongoDB, etc."
                disabled={loading}
              />
              <p className="text-xs text-slate-500">Séparer les technologies multiples avec des virgules</p>
            </div>

            {/* Tags */}
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-slate-700">
                <i className="bi bi-tags mr-2 text-slate-500"></i>
                Tags
              </label>
              <input
                type="text"
                name="tags"
                value={editFormData.tags}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-slate-300 rounded-lg transition-colors 
                         focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                placeholder="startup, fintech, b2b, etc."
                disabled={loading}
              />
              <p className="text-xs text-slate-500">Séparer les tags multiples avec des virgules</p>
            </div>

            {/* Contacts */}
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-slate-700">
                <i className="bi bi-envelope mr-2 text-slate-500"></i>
                Contacts Emails
              </label>
              <input
                type="text"
                name="contacts"
                value={editFormData.contacts}
                onChange={handleInputChange}
                className={`w-full px-4 py-3 border rounded-lg transition-colors focus:outline-none focus:ring-2 
                           ${errors.contacts 
                             ? 'border-red-300 focus:ring-red-500 focus:border-red-500' 
                             : 'border-slate-300 focus:ring-indigo-500 focus:border-indigo-500'
                           }`}
                placeholder="email1@example.com, email2@example.com"
                disabled={loading}
              />
              {errors.contacts && (
                <p className="text-sm text-red-600 flex items-center gap-1">
                  <i className="bi bi-exclamation-circle"></i>
                  {errors.contacts}
                </p>
              )}
              <p className="text-xs text-slate-500">Séparer les emails multiples avec des virgules</p>
            </div>

            {/* Contract Signed */}
            <div className="space-y-2">
              <label className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg border border-slate-200 cursor-pointer hover:bg-slate-100 transition-colors">
                <input
                  type="checkbox"
                  name="contractSigned"
                  checked={editFormData.contractSigned}
                  onChange={handleInputChange}
                  disabled={loading}
                  className="w-5 h-5 text-indigo-600 border-slate-300 rounded focus:ring-indigo-500 focus:ring-2"
                />
                <div className="flex items-center gap-2">
                  <i className="bi bi-file-earmark-check text-slate-600"></i>
                  <span className="text-sm font-semibold text-slate-700">Contrat Signé</span>
                </div>
              </label>
            </div>
          </form>
        </div>

        {/* Footer */}
        <div className="flex-shrink-0 px-6 py-4 bg-gray-50 border-t border-slate-200 flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={handleClose}
            disabled={loading}
            className="px-4 py-2 text-slate-600 hover:text-slate-800 font-medium transition-colors 
                     disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Annuler
          </button>
          
          <button
            type="submit"
            form="edit-partner-form"
            onClick={handleEditFormSubmit}
            disabled={loading}
            className="inline-flex items-center gap-2 px-6 py-2 bg-indigo-600 hover:bg-indigo-700 
                     text-white font-semibold rounded-lg transition-colors disabled:opacity-50 
                     disabled:cursor-not-allowed"
          >
            {loading ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                Enregistrement...
              </>
            ) : (
              <>
                <i className="bi bi-check-lg"></i>
                Enregistrer les modifications
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditPartnerForm;